import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { useUnsavedGuard, validForm } from '../ui/unsaved';
import { Notice } from '../ui/Notice';

const MIN_LENGTH = 10;

function FieldError({ error, path }: { error: unknown; path: string }) {
  if (!(error instanceof ApiError)) return null;
  const issue = fieldIssues(error).find((i) => i.path === path);
  return issue ? <span className="field-error">{issue.message}</span> : null;
}

/** The logged-in user changes their own password; other devices are signed out by the server. */
export function ChangePasswordPage() {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', repeat: '' });
  const [mismatch, setMismatch] = useState(false);
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () =>
      api('/api/auth/password', {
        method: 'POST',
        body: { currentPassword: form.currentPassword, newPassword: form.newPassword },
      }),
    onSuccess: () => {
      setForm({ currentPassword: '', newPassword: '', repeat: '' });
      setDone(true);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setDone(false);
    if (form.newPassword !== form.repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    change.mutate();
  }

  // owner 2026-10-23 21:14: leaving with something typed asks first
  const guardRef = useUnsavedGuard({
    dirty: Object.values(form).some((v) => v !== ''),
    save: async (el) => {
      if (form.newPassword !== form.repeat) {
        setMismatch(true);
        return false;
      }
      if (!validForm(el)) return false;
      await change.mutateAsync();
      return true;
    },
  });
  const error = change.error;
  const showGeneral = error instanceof ApiError && !(error.error === 'validation' && fieldIssues(error).length > 0);

  return (
    <section className="page narrow" data-feature-id="identity.password.change">
      <div className="page-head">
        <h1>Zmień hasło</h1>
      </div>
      <p className="muted lede">Po zmianie hasła zostaniesz zalogowany tylko na tym urządzeniu. Na pozostałych urządzeniach trzeba będzie zalogować się ponownie.</p>
      {done && (
        <Notice onClose={() => setDone(false)}>
          Hasło zostało zmienione. Inne urządzenia zostały wylogowane.
        </Notice>
      )}
      <form ref={guardRef} className="panel form" onSubmit={submit} aria-label="Zmiana hasła">
        {showGeneral && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <label className="field">
          <span>Obecne hasło</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={form.currentPassword}
            onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
          />
          <FieldError error={error} path="currentPassword" />
        </label>
        <label className="field">
          <span>Nowe hasło</span>
          <input
            type="password"
            autoComplete="new-password"
            minLength={MIN_LENGTH}
            required
            value={form.newPassword}
            onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
          />
          <small className="hint">Co najmniej {MIN_LENGTH} znaków.</small>
          <FieldError error={error} path="newPassword" />
        </label>
        <label className="field">
          <span>Powtórz nowe hasło</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            value={form.repeat}
            onChange={(e) => setForm({ ...form, repeat: e.target.value })}
          />
          {mismatch && <span className="field-error">Hasła nie są takie same</span>}
        </label>
        <div className="actions">
          <button className="btn primary" type="submit" disabled={change.isPending}>
            {change.isPending ? 'Zapisywanie…' : 'Zmień hasło'}
          </button>
        </div>
      </form>
    </section>
  );
}
